import { useState } from "react";
import { api, getToken, type Named, type Release, type ServerConfig, type Strategy } from "../lib/api.js";
import { bytes } from "../lib/format.js";
import { Drawer, Notice, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

interface BuildResult {
  id: string;
  filename: string;
  password: string | null;
  download_url: string;
  script_url: string;
  config_string: string;
}

const PLATFORM_LABEL: Record<string, string> = {
  windows: "Windows", macos: "macOS", linux: "Linux", android: "Android",
};

export default function Generator({ onToggleRail }: PageProps) {
  const cfg = useAsync(() => api.get<ServerConfig>("/api/console/generator/config"), []);
  const releases = useAsync(() => api.get<Release[]>("/api/console/generator/releases"), []);
  const strategies = useAsync(() => api.get<Strategy[]>("/api/console/strategies"), []);
  const deviceGroups = useAsync(() => api.get<Named[]>("/api/console/device-groups"), []);

  const [tag, setTag] = useState("");
  const [asset, setAsset] = useState("");
  const [setPassword, setSetPassword] = useState(true);
  const [password, setPassword2] = useState("");
  const [strategyName, setStrategyName] = useState("");
  const [deviceGroupName, setDeviceGroupName] = useState("");
  const [result, setResult] = useState<BuildResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const release = (releases.data ?? []).find((r) => r.tag === tag) ?? releases.data?.[0];
  const activeTag = tag || release?.tag || "";

  async function build() {
    setBusy(true); setError(""); setResult(null);
    try {
      // A deploy token lets the install script enrol the machine without a
      // console login on the target.
      let deployTokenId: string | null = null;
      if (strategyName || deviceGroupName) {
        const dt = await api.post<{ id: string }>("/api/console/deploy-tokens", {
          name: `Installer ${activeTag}`,
        });
        deployTokenId = dt.id;
      }
      const res = await api.post<BuildResult>("/api/console/generator/builds", {
        tag: activeTag,
        asset,
        generate_password: setPassword && !password,
        password: setPassword ? password || undefined : undefined,
        strategy_name: strategyName || undefined,
        device_group_name: deviceGroupName || undefined,
        deploy_token_id: deployTokenId,
      });
      setResult(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const grouped = new Map<string, Release["assets"]>();
  for (const a of release?.assets ?? []) {
    const arr = grouped.get(a.platform) ?? [];
    arr.push(a);
    grouped.set(a.platform, arr);
  }

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title"><h1>Installers</h1></div>
      </header>

      <div className="content">
        <div className="stack" style={{ maxWidth: 780 }}>
          {error ? <Notice>{error}</Notice> : null}
          {cfg.error ? <Notice>{cfg.error}</Notice> : null}

          {cfg.data && !cfg.data.complete ? (
            <Notice kind="warn">
              Set the ID server address under Server before building an installer, or the
              client will not know where to connect.
            </Notice>
          ) : null}

          <div className="panel">
            <div className="panel__head"><h2>Build a preconfigured installer</h2></div>
            <div className="panel__body">
              <p className="faint small">
                The installer carries this server&rsquo;s address in its filename, so the machine
                connects here the moment it is installed. Nothing has to be typed in on the machine.
              </p>

              {releases.loading ? <p className="faint">Loading available versions…</p> : null}
              {releases.error ? <Notice>{releases.error}</Notice> : null}

              {releases.data?.length ? (
                <>
                  <div className="field">
                    <label className="field__label">Version</label>
                    <select value={activeTag} onChange={(e) => { setTag(e.target.value); setAsset(""); }}>
                      {releases.data.map((r) => (
                        <option key={r.tag} value={r.tag}>{r.tag}</option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label className="field__label">Platform</label>
                    <select value={asset} onChange={(e) => setAsset(e.target.value)}>
                      <option value="">Choose a download</option>
                      {[...grouped.entries()].map(([platform, assets]) => (
                        <optgroup key={platform} label={PLATFORM_LABEL[platform] ?? platform}>
                          {assets.map((a) => (
                            <option key={a.name} value={a.name}>
                              {a.name} ({bytes(a.size)})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <label className="check">
                    <input type="checkbox" checked={setPassword} onChange={(e) => setSetPassword(e.target.checked)} />
                    Set a permanent password during install
                  </label>
                  {setPassword ? (
                    <div className="field">
                      <label className="field__label">Password</label>
                      <input className="mono" value={password} onChange={(e) => setPassword2(e.target.value)}
                        placeholder="Leave blank to generate a strong one" />
                      <span className="field__hint">
                        Applied by the install script, which needs administrator rights on the machine.
                      </span>
                    </div>
                  ) : null}

                  <div className="field">
                    <label className="field__label">Put the machine in this group</label>
                    <select value={deviceGroupName} onChange={(e) => setDeviceGroupName(e.target.value)}>
                      <option value="">Do not assign</option>
                      {(deviceGroups.data ?? []).map((g) => (
                        <option key={g.id} value={g.name}>{g.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label className="field__label">Apply this settings profile</label>
                    <select value={strategyName} onChange={(e) => setStrategyName(e.target.value)}>
                      <option value="">Do not assign</option>
                      {(strategies.data ?? []).map((s) => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  <button className="btn btn--primary" onClick={build} disabled={busy || !asset}>
                    {busy ? "Preparing…" : "Build installer"}
                  </button>
                </>
              ) : null}
            </div>
          </div>

          {cfg.data ? (
            <div className="panel">
              <div className="panel__head"><h2>What the installer will point at</h2></div>
              <div className="panel__body">
                <dl className="kv">
                  <dt>ID server</dt><dd className="mono">{cfg.data.host || <span className="faint">Not set</span>}</dd>
                  <dt>Relay</dt><dd className="mono">{cfg.data.relay || <span className="faint">Same as ID server</span>}</dd>
                  <dt>API</dt><dd className="mono">{cfg.data.api || <span className="faint">Derived from ID server</span>}</dd>
                  <dt>Key</dt><dd className="mono truncate">{cfg.data.key || <span className="faint">No key (anyone may connect)</span>}</dd>
                </dl>
                <p className="small faint" style={{ marginTop: "0.75rem", marginBottom: 0 }}>
                  Already have RustDesk installed? Paste this into Settings &rsaquo; Network &rsaquo; Import:
                </p>
                <code className="mono small" style={{ wordBreak: "break-all" }}>{cfg.data.config_string}</code>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {result ? (
        <Drawer title="Installer ready" onClose={() => setResult(null)}
          footer={<button className="btn" onClick={() => setResult(null)}>Done</button>}>
          <div className="stack">
            {result.password ? (
              <Notice kind="warn">
                <strong>Password: <code className="mono">{result.password}</code></strong>
                <div className="small" style={{ marginTop: "0.25rem" }}>
                  Copy it now. It is shown only once.
                </div>
              </Notice>
            ) : null}

            <div className="panel">
              <div className="panel__body">
                <h3 style={{ marginBottom: "0.5rem" }}>Unattended install (recommended)</h3>
                <p className="small faint">
                  One file that downloads, installs, sets the password and enrols the machine.
                  Run it as administrator on the target.
                </p>
                <a className="btn btn--primary" href={withToken(result.script_url)} download>
                  Download the install script
                </a>
              </div>
            </div>

            <div className="panel">
              <div className="panel__body">
                <h3 style={{ marginBottom: "0.5rem" }}>Just the installer</h3>
                <p className="small faint">
                  Preconfigured, but you will set the password yourself.
                  Keep the filename: it carries the server address.
                </p>
                <code className="mono small" style={{ wordBreak: "break-all", display: "block", marginBottom: "0.5rem" }}>
                  {result.filename}
                </code>
                <a className="btn" href={withToken(result.download_url)} download>
                  Download the installer
                </a>
              </div>
            </div>
          </div>
        </Drawer>
      ) : null}
    </>
  );
}

/**
 * Downloads are plain navigations, which cannot carry an Authorization
 * header, so the token travels as a query parameter instead.
 */
function withToken(url: string): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(getToken())}`;
}
